import {objectResolver} from 'airship/utils/resolvers'
import {ResolveError} from 'airship/utils/resolvers/errors'
import type {Resolver} from 'airship/utils/resolvers/types'

// Must be kept in sync with airship/apps/features/permissions.py#Feature
export enum Features {
  MessageNav = 'has_messages_nav_access',
  MessageOverview = 'has_messages_overview',
  ContentTemplates = 'has_content_templates_access',
  PushTemplates = 'has_push_templates_access',
  AppleNewsComposer = 'has_apple_news_composer_access',
  ActivityLog = 'has_activity_log_access',
  PushComposer = 'has_pcomposer_access',
  MessageCenter = 'has_message_center_enabled',
  Configuration = 'has_configuration_access',
  JourneysRead = 'has_journeys_composer_access',
  JourneysWrite = 'has_journeys_publishing_access',
  SettingsPage = 'has_settings_page_access',
  Reports = 'has_reports_access',
  ReportsOptin = 'has_optin_access',
  ReportsResponse = 'has_response_report_access',
  ReportsAudience = 'has_audience_reports_access',
  ReportsEmail = 'has_email_reports_access',
  ReportsSms = 'has_sms_reports_access',
  ReportsWeb = 'has_web_push_reports_access',
  SmsWorkflows = 'has_sms_workflows_access',
  Survey = 'has_survey_access',
  Darkmode = 'has_dark_mode_access',
  CustomEventsRead = 'has_custom_events_read_access',
  Insight = 'has_insight_access',
  SegmentsRead = 'has_segments_read_access',
  StaticLists = 'has_static_lists_access',
  Tags = 'has_tags_page_access',
  DeviceLookup = 'has_device_lookup_access',
  SubscriptionListAutoOptIn = 'has_subscription_list_auto_optin',
  SubscriptionListActions = 'has_subscription_list_actions',
  MultiChannelPreferenceCenters = 'has_multichannel_preference_centers',
  AiEnabled = 'has_ai_enabled',
  Scene = 'has_scene_access',
  FeatureFlags = 'has_feature_flags_access',
  FeatureFlagsHomeAccess = 'has_feature_flags_home_access',
  FeatureFlagsEntitlements = 'has_feature_flags_entitlements',
  UnsubscribePages = 'has_unsubscribe_pages',
  HoldoutExperiments = 'has_holdout_experiments_access',
  Email = 'has_email_access',
  Sms = 'has_sms_access',
  Rtds = 'has_rtds_access',
  ExperimentsOverview = 'has_experiments_overview_access',
  Experiments = 'has_experiments_access',
  TriggersTag = 'has_triggers_tag_access',
  InAppAutomationComposer = 'has_in_app_automation_composer_access',
  InAppAutomationPublishing = 'has_in_app_automation_publishing_access',
  InAppAutomationDisplay = 'has_in_app_automation_display_access',
  IosIsOnline = 'ios_is_online',
  AndroidIsOnline = 'android_is_online',
  AmazonIsOnline = 'amazon_is_online',
  AppIsOnline = 'app_is_online',
  WebIsOnline = 'web_is_online',
  EmailIsOnline = 'email_is_online',
  SmsIsOnline = 'sms_is_online',
  MmsIsOnline = 'mms_is_online',
  ConfigureBanLists = 'has_ban_list_config_access',
  BanLists = 'has_ban_list_access',
  WebPages = 'has_web_pages_access',
  ImageHostingAccess = 'has_image_hosting_access',
  ScenesEnabled = 'has_scenes_enabled',
  JourneysEnabled = 'has_journeys_enabled',
  ABTestsEnabled = 'has_ab_tests_enabled',
  HoldoutExperimentsEnabled = 'has_holdout_experiments_enabled',
  RfmAnalysis = 'has_rfm_analysis_access',
  RfmAnalysisSegmentation = 'has_rfm_analysis_segmentation_access',
  WebScene = 'has_web_scene_access',
  ContentAuthor = 'has_content_author_access',
  UserSegmentationEnabled = 'has_user_segmentation_enabled',
  CanLinkFirstFlight = 'can_link_first_flight',
  HasApptimize = 'has_apptimize',
  BrandGuidelines = 'has_brand_guidelines_access',
  Xdf = 'has_xdf_access',
  PreferenceCenters = 'has_preference_centers_access',
  WebPush = 'has_web_push_access',
  OpenChannels = 'has_open_channels_access',
  MobilePlatform = 'has_mobile_platform_access',
  AppleNews = 'has_apple_news_access',
  AppSharing = 'has_app_sharing_access',
  Couponing = 'has_couponing_access',
  Orchestration = 'has_orchestration_app_level_access',
  OpenAi = 'has_openai_access',
  UrlParameters = 'has_url_parameters_access',
  PredictiveAI = 'has_predictive_ai',
  Credentials = 'has_app_credentials_access',
  Windows = 'has_windows_configured',
  BrandGuidelinesEditing = 'has_brand_guidelines_editing_access',
  CanDeleteProject = 'can_delete_project',
  ErrorConsole = 'has_error_console_access',
  Bees = 'has_bees_toggle',
}

export type ResolveFeaturesResponse<T extends Features> = {
  features: Record<T, boolean>
}

export function createFeaturesResolver<T extends Features>(
  features: Readonly<T[]>
): Resolver<ResolveFeaturesResponse<T>> {
  const resolveFeatures: Resolver<Record<T, boolean>> = (data: unknown) => {
    if (!(data instanceof Object)) {
      throw new ResolveError([
        {
          message: `Expected Object, got ${typeof data}`,
          location: '',
          value: data,
        },
      ])
    }
    for (const feature of features) {
      const value = (data as Record<T, boolean>)[feature]
      if (typeof value !== 'boolean') {
        throw new ResolveError([
          {
            message: `Expected Boolean, got ${typeof value}`,
            location: '',
            value: data,
          },
        ])
      }
    }

    return data as Record<T, boolean>
  }
  return objectResolver({
    features: resolveFeatures,
  })
}
